/**
 * StaffServiceCatalog — Verwaltung der Service-Definitionen (admin-api Catalog).
 *
 * Migriert von admin-portal/src/pages/{ServiceCatalog,ServiceDetail}/.
 * TQ #9696 (Staff-Migration Teil C, Bundle #9523): weiter portiert aus
 * customer-portal/src/pages/staff/StaffServiceCatalog.tsx nach org-portal.
 * useStaffApi/@it-sce/ui-Framework (DataTable/useApiResource/useApiMutation)
 * unveraendert uebernommen; useToast auf org-portal-Konvention umgestellt
 * (../../components/ui → showToast(type, msg) via lokalem toast-Adapter).
 *
 * Workflows:
 *   - Liste mit Suche (Name/Slug/ItemCode) + Category-Filter + Visibility-Filter
 *   - Inline-Toggle: Visible / Active / Featured pro Zeile
 *   - DisplayOrder editieren via Detail-Drawer
 *   - Detail-Drawer (rechts) zeigt: Stammdaten, Sync-Status, Audit-Log
 *   - "Sync jetzt" triggert ERPNext-Sync und refetcht
 */

import { useMemo, useState } from 'react'
import {
  PageHeader,
  DataTable,
  ConfirmDialog,
  AuditTimeline,
  useApiResource,
  useApiMutation,
  type DataTableFilterOption,
} from '@it-sce/ui'
import {
  type TServiceCatalogEntry,
  type TServiceCatalogUpdateRequest,
} from '@it-sce/ui'
import { useStaffApi } from '../../hooks/useStaffApi'
import { useToast } from '../../components/ui'

type VisibilityFilter = 'all' | 'visible' | 'hidden'

const VIS_OPTIONS: DataTableFilterOption[] = [
  { value: 'all', label: 'Alle' },
  { value: 'visible', label: 'Sichtbar' },
  { value: 'hidden', label: 'Versteckt' },
]

function formatPrice(s: TServiceCatalogEntry): string {
  if (s.basePrice == null) return '–'
  return `${s.basePrice.toFixed(2)} ${s.currency} / ${s.billingCycle}`
}

function formatDate(iso?: string | null): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function ToggleSwitch({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label?: string
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        onChange(!checked)
      }}
      style={{
        width: 36, height: 20, borderRadius: 10,
        border: 'none',
        background: checked ? '#10b981' : '#d1d5db',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.15s',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 2, left: checked ? 18 : 2,
          width: 16, height: 16,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left 0.15s',
        }}
      />
    </button>
  )
}

export default function StaffServiceCatalog() {
  const staff = useStaffApi()
  const { showToast } = useToast()
  const toast = {
    success: (m: string) => showToast('success', m),
    error: (m: string) => showToast('error', m),
  }

  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('all')
  const [visFilter, setVisFilter] = useState<VisibilityFilter>('all')
  const [selected, setSelected] = useState<TServiceCatalogEntry | null>(null)
  const [confirmSync, setConfirmSync] = useState(false)

  const { data, loading, error, refetch } = useApiResource(
    ['staff-services'],
    () => staff.services.list()
  )

  const update = useApiMutation(
    ({ id, req }: { id: number; req: TServiceCatalogUpdateRequest }) =>
      staff.services.update(id, req),
    {
      onSuccess: () => {
        toast.success('Aktualisiert')
        void refetch()
      },
      onError: (e) => toast.error(`Aktualisierung fehlgeschlagen: ${e.message}`),
    }
  )

  const sync = useApiMutation(
    () => staff.services.triggerSync(),
    {
      onSuccess: (res) => {
        toast.success(`Sync ${res.synced}/${res.totalServices} OK · ${res.errors} Fehler`)
        setConfirmSync(false)
        void refetch()
      },
      onError: (e) => {
        toast.error(`Sync fehlgeschlagen: ${e.message}`)
        setConfirmSync(false)
      },
    }
  )

  const services = data?.services ?? []

  const categories = useMemo(() => {
    const set = new Set<string>()
    services.forEach((s) => s.category && set.add(s.category))
    return Array.from(set).sort()
  }, [services])

  const categoryOptions: DataTableFilterOption[] = useMemo(
    () => [
      { value: 'all', label: 'Alle Kategorien' },
      ...categories.map((c) => ({ value: c, label: c })),
    ],
    [categories]
  )

  const filterPredicate = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (s: TServiceCatalogEntry) => {
      if (categoryFilter !== 'all' && s.category !== categoryFilter) return false
      if (visFilter === 'visible' && !s.isVisible) return false
      if (visFilter === 'hidden' && s.isVisible) return false
      if (!q) return true
      return [s.name, s.slug ?? '', s.erpnextItemCode, s.description ?? '']
        .join(' ').toLowerCase().includes(q)
    }
  }, [search, categoryFilter, visFilter])

  return (
    <div className="page-content">
      <PageHeader
        title="Service-Katalog"
        icon="📦"
        subtitle={`${services.length} Services · Sichtbarkeit + Reihenfolge verwalten, Audit-Log einsehen`}
        actions={
          <button
            type="button"
            onClick={() => setConfirmSync(true)}
            disabled={sync.loading}
            style={{
              padding: '0.5rem 1rem',
              background: '#0ea5e9',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            ↻ Sync von ERPNext
          </button>
        }
      />

      <DataTable<TServiceCatalogEntry>
        columns={[
          {
            key: 'name',
            header: 'Service',
            sortable: true,
            render: (s) => (
              <div>
                <div style={{ fontWeight: 600 }}>{s.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  <code>{s.erpnextItemCode}</code>
                  {s.slug && <> · <code>{s.slug}</code></>}
                </div>
              </div>
            ),
          },
          {
            key: 'category',
            header: 'Kategorie',
            sortable: true,
            width: '140px',
          },
          {
            key: 'price',
            header: 'Preis',
            align: 'right',
            width: '160px',
            render: formatPrice,
          },
          {
            key: 'displayOrder',
            header: 'Reihenfolge',
            align: 'center',
            width: '110px',
            sortable: true,
            render: (s) => s.displayOrder,
          },
          {
            key: 'isVisible',
            header: 'Sichtbar',
            align: 'center',
            width: '100px',
            render: (s) => (
              <ToggleSwitch
                checked={s.isVisible}
                onChange={(next) => update.mutate({ id: s.id, req: { isVisible: next } }).catch(() => {})}
                disabled={update.loading}
                label={`${s.name} sichtbar`}
              />
            ),
          },
          {
            key: 'isActive',
            header: 'Aktiv',
            align: 'center',
            width: '90px',
            render: (s) => (
              <ToggleSwitch
                checked={s.isActive}
                onChange={(next) => update.mutate({ id: s.id, req: { isActive: next } }).catch(() => {})}
                disabled={update.loading}
                label={`${s.name} aktiv`}
              />
            ),
          },
          {
            key: 'isFeatured',
            header: 'Empfohlen',
            align: 'center',
            width: '110px',
            render: (s) => (
              <ToggleSwitch
                checked={s.isFeatured}
                onChange={(next) => update.mutate({ id: s.id, req: { isFeatured: next } }).catch(() => {})}
                disabled={update.loading}
                label={`${s.name} empfohlen`}
              />
            ),
          },
          {
            key: 'syncStatus',
            header: 'Sync',
            width: '110px',
            render: (s) => (
              <div style={{ fontSize: '0.75rem' }}>
                <div>{s.syncStatus}</div>
                <div style={{ color: '#9ca3af' }}>{formatDate(s.lastSyncAt)}</div>
              </div>
            ),
          },
        ]}
        data={services}
        keyExtractor={(s) => String(s.id)}
        loading={loading}
        error={error}
        filterPredicate={filterPredicate}
        onRowClick={(s) => setSelected(s)}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Name, Slug, ItemCode…',
        }}
        filters={[
          {
            key: 'category',
            label: 'Kategorie',
            value: categoryFilter,
            options: categoryOptions,
            onChange: setCategoryFilter,
          },
          {
            key: 'visibility',
            label: 'Sichtbarkeit',
            value: visFilter,
            options: VIS_OPTIONS,
            onChange: (v) => setVisFilter(v as VisibilityFilter),
          },
        ]}
        onRefresh={() => void refetch()}
        emptyMessage={
          services.length === 0
            ? 'Keine Services im Katalog — Sync von ERPNext anstoßen'
            : 'Kein Service passt zu den Filtern'
        }
      />

      <ConfirmDialog
        isOpen={confirmSync}
        title="ERPNext-Sync starten?"
        variant="info"
        confirmLabel="Sync starten"
        loading={sync.loading}
        message={
          <>
            Holt alle Items aus ERPNext und aktualisiert den Katalog.
            Bestehende Visibility-, Active- und Reihenfolge-Einstellungen bleiben erhalten.
          </>
        }
        onConfirm={() => sync.mutate(undefined as unknown as void).catch(() => {})}
        onCancel={() => setConfirmSync(false)}
      />

      {selected && <ServiceDetailDrawer service={selected} onClose={() => setSelected(null)} onSaved={() => { void refetch() }} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Detail Drawer (Stammdaten + Audit)
// ---------------------------------------------------------------------------

function ServiceDetailDrawer({
  service,
  onClose,
  onSaved,
}: {
  service: TServiceCatalogEntry
  onClose: () => void
  onSaved: () => void
}) {
  const staff = useStaffApi()
  const { showToast } = useToast()
  const toast = {
    success: (m: string) => showToast('success', m),
    error: (m: string) => showToast('error', m),
  }

  const [displayOrder, setDisplayOrder] = useState<number>(service.displayOrder)
  const [description, setDescription] = useState<string>(service.description ?? '')

  const audit = useApiResource(
    ['staff-service-audit', service.id],
    () => staff.services.audit(service.id)
  )

  const update = useApiMutation(
    (req: TServiceCatalogUpdateRequest) => staff.services.update(service.id, req),
    {
      onSuccess: () => {
        toast.success('Gespeichert')
        onSaved()
      },
      onError: (e) => toast.error(`Speichern fehlgeschlagen: ${e.message}`),
    }
  )

  const dirty =
    displayOrder !== service.displayOrder ||
    description !== (service.description ?? '')

  const save = async () => {
    const req: TServiceCatalogUpdateRequest = {}
    if (displayOrder !== service.displayOrder) req.displayOrder = displayOrder
    if (description !== (service.description ?? '')) req.description = description
    if (Object.keys(req).length === 0) return
    await update.mutate(req).catch(() => {})
  }

  return (
    <div
      role="dialog"
      aria-label={`Service ${service.name} Details`}
      style={{
        position: 'fixed',
        top: 0, right: 0, bottom: 0,
        width: 480,
        background: '#fff',
        boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100,
      }}
    >
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{service.name}</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>
            <code>{service.erpnextItemCode}</code> · {service.category}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          style={{ border: 'none', background: 'transparent', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}
        >
          ×
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
        <section>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Stammdaten</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>Reihenfolge</span>
              <input
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value || '0', 10))}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.75rem', color: '#374151', fontWeight: 600 }}>Kurzbeschreibung</span>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={save}
                disabled={!dirty || update.loading}
                style={{
                  padding: '0.5rem 1rem',
                  background: dirty ? '#3b82f6' : '#9ca3af',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  cursor: dirty ? 'pointer' : 'not-allowed',
                }}
              >
                {update.loading ? '…' : 'Speichern'}
              </button>
            </div>
          </div>
        </section>

        <section style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Sync</h3>
          <dl style={{ margin: 0, fontSize: '0.8125rem', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.25rem 1rem' }}>
            <dt style={{ color: '#6b7280' }}>Status</dt><dd style={{ margin: 0 }}>{service.syncStatus}</dd>
            <dt style={{ color: '#6b7280' }}>Zuletzt</dt><dd style={{ margin: 0 }}>{formatDate(service.lastSyncAt)}</dd>
            <dt style={{ color: '#6b7280' }}>Erstellt</dt><dd style={{ margin: 0 }}>{formatDate(service.createdAt)}</dd>
            <dt style={{ color: '#6b7280' }}>Geändert</dt><dd style={{ margin: 0 }}>{formatDate(service.updatedAt)}</dd>
          </dl>
        </section>

        <section style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Audit-Log</h3>
          {audit.loading ? (
            <p style={{ color: '#6b7280', fontSize: '0.8125rem' }}>Laden…</p>
          ) : (
            <AuditTimeline
              entries={audit.data?.entries ?? []}
              timestampOf={(e) => e.changedAt}
              render={(e) => ({
                headline: <><strong>{e.field}</strong>: {e.oldValue ?? '–'} → {e.newValue ?? '–'}</>,
                meta: `${e.changedBy}`,
                body: e.changeReason,
              })}
              emptyMessage="Noch keine Audit-Einträge (Backend-Endpoint pending)"
            />
          )}
        </section>
      </div>
    </div>
  )
}
